import React from "react";
import { useEffect, useState } from "react";
import { BuilderComponent, builder, useIsPreviewing } from "@builder.io/react";
import { Box, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import Error404Page from "../../pages/errors/404";
import { SeoData } from "../../models/builderIO/types";
import SeoCommon from "@/app/utils/seo/seoCommon";
import SeoControl from "@/app/utils/seo/seoControl";
import "@builder.io/widgets";

interface BuilderIOSectionProps {
  section: string;
  data?: any; //should contain the data for the page
  context?: any; //should contain actions (functions) that can be called in the page
  onStateChange?: (state: any) => void;
  filter?: {
    name: string;
    value: string;
  };
  preventLoading?: boolean;
}

const BuilderIOSection: React.FC<BuilderIOSectionProps> = ({
  section,
  data,
  context,
  filter,
  onStateChange,
  preventLoading,
}) => {
  const [notFound, setNotFound] = useState(false);
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [seoCommon, setSeoCommon] = useState<SeoData | null>(null);
  const [fullState, setFullState] = useState();
  const isPreviewingInBuilder = useIsPreviewing();

  const isAuthenticatedFromStore = useSelector(
    (state: RootState) => state.userState.isAuthenticated
  );
  const [isAuthenticated, setIsAuthenticated] = React.useState(
    isAuthenticatedFromStore
  );

  const customerNumber = useSelector(
    (state: RootState) => state.userState.user?.customerNumber ?? null
  );
  const customerName = useSelector(
    (state: RootState) => state.userState.user?.customerName ?? null
  );

  const selectedWarehouse = useSelector(
    (state: RootState) => state.warehouseState?.selectedWarehouse?.warehouseCode
  );

  const customerData = {
    customerNumber: customerNumber,
    customerName: customerName,
  };

  const stateChangedHandler = (state: any) => {
    setIsAuthenticated(state.isAuthenticated);

    if (state) {
      setFullState(state);
    }

    if (state.seoCommon?.data) {
      setSeoCommon(state.seoCommon.data as SeoData);
    }

    if (onStateChange) {
      onStateChange(state);
    }

    console.log(`Builder.IO Section: ${section}`);
    console.log(state);
  };

  useEffect(() => {
    async function fetchContent() {
      setIsLoading(true);
      try {
        const content = await builder
          .get(section, {
            userAttributes: {
              urlPath: window.location.pathname,
            },
            ...(filter?.name && filter?.value
              ? { query: { [filter.name]: filter.value } }
              : {}),
          })
          .promise();

        setContent(content);
        setNotFound(!content);
      } catch (error) {
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    }
    fetchContent();
  }, [section, filter?.name, filter?.value, context]);

  if (isLoading && preventLoading !== true) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (notFound && !isPreviewingInBuilder) {
    return <Error404Page />;
  }

  const reactWebUrl = isAuthenticated
    ? process.env.REACT_APP_WEB_URL +
      "/" +
      process.env.REACT_APP_STOREFRONT_SUBDIRECTORY
    : process.env.REACT_APP_WEB_URL + "/" + process.env.REACT_APP_SUBDIRECTORY;

  const whitelistSeoSections = ["feature-zone-carousel"];

  return (
    <>
      {/* SEO Component */}
      {whitelistSeoSections.includes(section) && (
        <SeoControl
          noindex={
            seoCommon === null ||
            seoCommon === undefined ||
            seoCommon.noIndex === true
          }
          nofollow={
            seoCommon === null ||
            seoCommon === undefined ||
            seoCommon.noFollow === true
          }
        />
      )}
      {seoCommon && Object.keys(seoCommon).length > 0 && (
        <SeoCommon
          seoCommon={seoCommon}
          content={content}
          templateData={fullState}
        ></SeoCommon>
      )}
      {/* Render the Builder section */}
      <BuilderComponent
        model={section}
        context={context ? { ...context } : undefined}
        content={content!}
        onStateChange={stateChangedHandler}
        data={{
          ...data,
          isAuthenticated: isAuthenticated,
          apiUrl: process.env.REACT_APP_STOREFRONT_API_URL?.replace(
            "https://",
            ""
          ),
          apiBaseUrl: process.env.REACT_APP_BASE_API_URL?.replace("https://", ""),
          webUrl: process.env.REACT_APP_WEB_URL?.replace("https://", ""),
          reactWebUrl: reactWebUrl,
          baseWebUrl: process.env.REACT_APP_WEB_URL,
          ...(customerData && { customer: customerData }),
          selectedWarehouse: selectedWarehouse,
        }}
      />
    </>
  );
};

export default BuilderIOSection;
